<template>
  <view class="transport-record">
    <!-- 统计卡片 -->
    <view class="stats-card">
      <view class="stat-item">
        <text class="count">{{ stats.totalTransports }}</text>
        <text class="label">总转运单</text>
      </view>
      <view class="divider"></view>
      <view class="stat-item">
        <text class="count">{{ stats.pendingTransports }}</text>
        <text class="label">待出发</text>
      </view>
      <view class="divider"></view>
      <view class="stat-item">
        <text class="count">{{ stats.completedTransports }}</text>
        <text class="label">已完成</text>
      </view>
    </view>

    <!-- 筛选栏 -->
    <view class="filter-bar">
      <view class="type-filter">
        <text
          v-for="(type, index) in transportTypes"
          :key="index"
          :class="{ active: currentType == type.value }"
          @tap="selectType(type.value)"
          >{{ type.label }}</text
        >
      </view>

      <!-- 时间筛选 -->
      <view class="date-filter">
        <picker
          mode="date"
          :value="startDate"
          :end="endDate"
          @change="onStartDateChange"
        >
          <text>{{ startDate || "开始日期" }}</text>
        </picker>
        <text class="separator">至</text>
        <picker
          mode="date"
          :value="endDate"
          :start="startDate"
          @change="onEndDateChange"
        >
          <text>{{ endDate || "结束日期" }}</text>
        </picker>
      </view>
    </view>

    <!-- 转运单列表 -->
    <scroll-view
      scroll-y
      class="transport-list"
      refresher-enabled
      :refresher-triggered="refreshing"
      @refresherrefresh="onRefresh"
      @scrolltolower="onLoadMore"
    >
      <view
        class="transport-item card"
        v-for="(item, index) in filteredTransports"
        :key="index"
        @tap="viewDetail(item)"
      >
        <view class="header">
          <view class="case-info">
            <view class="info">
              <text class="case-no">{{ item.caseNo || item.reportId }}</text>
              <text class="patient"
                >{{ item.patName }} | {{ item.sex == 1 ? "男" : "女" }} |
                {{ item.age }}岁</text
              >
            </view>
          </view>
          <text class="status" :class="getStatusClass(item.transitStatus)">{{
            getStatusText(item.transitStatus)
          }}</text>
        </view>

        <view class="detail-info">
          <view class="info-item">
            <text class="label">疾病诊断</text>
            <text class="value">{{ item.diagnosisname || "未填写" }}</text>
          </view>
          <view class="info-item">
            <text class="label">所在医疗机构</text>
            <text class="value">{{
              item.treatmentHospitalName || "未填写"
            }}</text>
          </view>
          <view class="info-item">
            <text class="label">计划转运时间</text>
            <text class="value">{{ formatTime(item.transportStartTime) }}</text>
          </view>
          <view class="info-item">
            <text class="label">负责协调员</text>
            <text class="value">{{ item.contactPerson || "未指定" }}</text>
          </view>
          <view class="info-item">
            <text class="label">创建时间</text>
            <text class="value">{{ formatTime(item.createTime) }}</text>
          </view>
        </view>

        <view class="footer">
          <view class="action-info">
            <text class="label">转运状态</text>
            <text class="transport-status">{{
              getStatusText(item.transitStatus)
            }}</text>
          </view>
          <view class="actions">
            <!-- 待转运(1)：可以开始转运 -->
            <button
              class="action-btn primary"
              v-if="item.transitStatus == 1"
              @tap.stop="handleStartTransport(item)"
            >
              开始转运
            </button>

            <!-- 转运中(2)：可以完成或取消 -->
            <button
              class="action-btn success"
              v-if="item.transitStatus == 2"
              @tap.stop="handleCompleteTransport(item)"
            >
              完成转运
            </button>
            <button
              class="action-btn danger"
              v-if="item.transitStatus == 2"
              @tap.stop="handleCancelTransport(item)"
            >
              取消转运
            </button>

            <!-- 已取消(4)：可以恢复 -->
            <button
              class="action-btn warning"
              v-if="item.transitStatus == 4"
              @tap.stop="handleRecoverTransport(item)"
            >
              恢复转运
            </button>

            <!-- 通用按钮 -->
            <button class="action-btn secondary" @tap.stop="viewDetails(item)">
              查看详情
            </button>
          </view>
        </view>
      </view>

      <!-- 加载更多 -->
      <view class="load-more" v-if="loadingMore">
        <u-loading-icon text="加载中..."></u-loading-icon>
      </view>

      <!-- 无更多数据 -->
      <view class="no-more" v-if="!hasMore && filteredTransports.length > 0">
        <text>已加载全部数据</text>
      </view>

      <!-- 空状态 -->
      <view
        class="empty-state"
        v-if="!loading && filteredTransports.length == 0"
      >
        <image src="/static/empty/no-transport.png" mode="aspectFit" />
        <text>暂无转运单记录</text>
      </view>
    </scroll-view>

    <!-- 操作确认弹窗 -->
    <view class="modal-mask" v-if="showActionModal" @tap="cancelAction">
      <view class="modal-content" @tap.stop>
        <view class="modal-header">
          <text class="modal-title">{{ modalTitle }}</text>
        </view>
        <view class="modal-body">
          <text class="modal-desc">
            确定要{{ modalAction }}转运单 "{{ currentTransport.caseNo }}" 吗？
          </text>
          <view class="modal-summary" v-if="currentTransport.patName">
            <text class="summary-item"
              >患者：{{ currentTransport.patName }}</text
            >
            <text class="summary-item"
              >当前状态：{{
                getStatusText(currentTransport.transitStatus)
              }}</text
            >
          </view>
        </view>
        <view class="modal-footer">
          <button class="modal-btn cancel" @tap="cancelAction">取消</button>
          <button class="modal-btn confirm" @tap="confirmAction">
            确认{{ modalAction }}
          </button>
        </view>
      </view>
    </view>

    <!-- 加载状态 -->
    <u-loading-page v-if="initLoading" :loading="true" text="数据加载中..." />
  </view>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { onLoad, onShow, onReachBottom } from "@dcloudio/uni-app";
import { useDict } from "@/utils/dict";

// 字典数据
const dict = ref({});

// 统计数据
const stats = ref({
  totalTransports: 0,
  pendingTransports: 0,
  completedTransports: 0,
});

// 转运单数据
const transports = ref([]);
const loading = ref(false);
const initLoading = ref(false);
const loadingMore = ref(false);
const refreshing = ref(false);
const hasMore = ref(true);
const pageNum = ref(1);
const pageSize = ref(10);

// 操作确认弹窗相关
const showActionModal = ref(false);
const currentTransport = ref({});
const modalTitle = ref("");
const modalAction = ref("");
const modalTargetStatus = ref(null);

// 筛选相关
const transportTypes = [
  { label: "全部", value: "" },
  { label: "待转运", value: 1 },
  { label: "转运中", value: 2 },
  { label: "已完成", value: 3 },
  { label: "已取消", value: 4 },
  { label: "暂存", value: 5 },
];
const currentType = ref("");
const startDate = ref("");
const endDate = ref("");

onLoad(async (options) => {
  // 获取字典数据
  dict.value = await useDict("sys_user_sex");
  // 初始化数据
  await loadInitialData();
});

onReachBottom(() => {
  if (hasMore.value && !loading.value && !loadingMore.value) {
    onLoadMore();
  }
});

onShow(() => {
  // 检查是否有转运状态更新
  const update = uni.getStorageSync("transportStatusUpdate");
  if (update) {
    handleStatusUpdate(update);
    uni.removeStorageSync("transportStatusUpdate");
  }
});

// 数据映射函数
const mapApiDataToTransportItem = (apiData) => {
  return {
    id: apiData.id,
    reportId: apiData.reportId,
    caseNo: apiData.caseNo,
    patName: apiData.patName,
    sex: apiData.sex,
    age: apiData.age,
    diagnosisname: apiData.diagnosisname,
    treatmentHospitalName: apiData.treatmentHospitalName,
    transportStartTime: apiData.transportStartTime,
    contactPerson: apiData.contactPerson,
    createTime: apiData.createTime,
    transitStatus: apiData.transitStatus,
    // 医护人员信息
    doctor: apiData.doctor,
    nurse: apiData.nurse,
    driver: apiData.driver,
    // 其他字段
    transportStartPlace: apiData.transportStartPlace,
    remark: apiData.remark,
    annexfilesList: apiData.annexfilesList || [],
  };
};

// 计算属性：筛选记录
const filteredTransports = computed(() => {
  let result = transports.value;

  // 前端状态筛选
  if (currentType.value !== "") {
    result = result.filter((item) => item.transitStatus == currentType.value);
  }

  return result;
});

// 获取状态样式
const getStatusClass = (status) => {
  const map = {
    1: "pending", // 待转运
    2: "transporting", // 转运中
    3: "completed", // 已完成
    4: "cancelled", // 已取消
    5: "cancelled", // 暂存
  };
  return map[status] || "pending";
};

// 获取状态文本
const getStatusText = (status) => {
  const map = {
    1: "待出发",
    2: "转运中",
    3: "已完成",
    4: "已取消",
    5: "暂存",
  };
  return map[status] || "待转运";
};

// 获取性别文本
const getGenderText = (gender) => {
  if (!gender) return "待转运";
  if (!dict.value.sys_user_sex) return gender;
  const genderItem = dict.value.sys_user_sex.find(
    (item) => item.dictValue == gender,
  );
  return genderItem ? genderItem.dictLabel : gender;
};

// 格式化时间
const formatTime = (timeStr) => {
  if (!timeStr) return "未设置";
  return timeStr.replace("T", " ").substring(0, 16);
};

// 选择类型
const selectType = (type) => {
  currentType.value = type;
  // 重置分页，重新加载数据
  pageNum.value = 1;
  loadTransports(true);
};

// 日期选择
const onStartDateChange = (e) => {
  startDate.value = e.detail.value;
  // 如果结束日期已选择，立即触发筛选
  if (endDate.value) {
    pageNum.value = 1;
    loadTransports(true);
  }
};

const onEndDateChange = (e) => {
  endDate.value = e.detail.value;
  // 如果开始日期已选择，立即触发筛选
  if (startDate.value) {
    pageNum.value = 1;
    loadTransports(true);
  }
};

// ============== 转运状态变更操作 ==============

/** 开始转运 */
const handleStartTransport = (item) => {
  currentTransport.value = { ...item };
  modalTitle.value = "开始转运";
  modalAction.value = "开始";
  modalTargetStatus.value = 2; // 设置为转运中
  showActionModal.value = true;
};

/** 完成转运 */
const handleCompleteTransport = (item) => {
  currentTransport.value = { ...item };
  modalTitle.value = "完成转运";
  modalAction.value = "完成";
  modalTargetStatus.value = 3; // 设置为转运完成
  showActionModal.value = true;
};

/** 取消转运 */
const handleCancelTransport = (item) => {
  currentTransport.value = { ...item };
  modalTitle.value = "取消转运";
  modalAction.value = "取消";
  modalTargetStatus.value = 4; // 设置为转运取消
  showActionModal.value = true;
};

/** 恢复转运 */
const handleRecoverTransport = (item) => {
  currentTransport.value = { ...item };
  modalTitle.value = "恢复转运";
  modalAction.value = "恢复";
  modalTargetStatus.value = 2; // 设置为转运中
  showActionModal.value = true;
};

// 取消操作
const cancelAction = () => {
  showActionModal.value = false;
};

// 确认操作
const confirmAction = async () => {
  try {
    const requestData = {
      ...currentTransport.value,
      transitStatus: modalTargetStatus.value,
    };

    // 清空附件ID（参照PC端逻辑）
    if (requestData.annexfilesList && requestData.annexfilesList.length > 0) {
      requestData.annexfilesList.forEach((item) => {
        item.id = null;
      });
    }

    const res = await uni.$uapi.post("/project/transport/edit", requestData);

    if (res && res.code == 200) {
      uni.showToast({
        title: `${modalAction.value}转运成功`,
        icon: "success",
      });

      // 如果是完成转运（状态变为3），同步更新案例状态
      if (modalTargetStatus.value == 3) {
        try {
          // 获取案例详情
          const caseRes = await uni.$uapi.get(
            `/project/donatebaseinforeport/getInfo/${requestData.reportId}`,
          );

          if (caseRes && caseRes.data) {
            const caseData = caseRes.data;
            caseData.isDonate = 1; // 标记为已捐献

            const editRes = await uni.$uapi.post(
              "/project/donatebaseinforeport/edit",
              caseData,
            );

            if (editRes && editRes.code == 200) {
              uni.showToast({
                title: "对应上报案例已进入正式案例流程",
                icon: "success",
              });
            }
          }
        } catch (caseError) {
          console.error("更新案例状态失败:", caseError);
          // 不阻断主流程，只提示
          uni.showToast({
            title: "案例状态更新失败，请联系管理员",
            icon: "none",
          });
        }
      }

      // 更新本地数据
      const index = transports.value.findIndex(
        (item) => item.id == currentTransport.value.id,
      );
      if (index !== -1) {
        transports.value[index].transitStatus = modalTargetStatus.value;
      }

      // 刷新统计数据
      await loadStats();

      // 关闭弹窗
      showActionModal.value = false;
    } else {
      uni.showToast({
        title: res?.msg || `${modalAction.value}转运失败`,
        icon: "none",
      });
    }
  } catch (error) {
    console.error(`${modalAction.value}转运失败:`, error);
    uni.showToast({
      title: "操作失败，请稍后重试",
      icon: "none",
    });
  }
};

// 查看详情
const viewDetail = (item) => {
  viewDetails(item);
};

// 查看详情
const viewDetails = (item) => {
  uni.navigateTo({
    url: `/pagesSub/case/transferinfo?id=${item.id}`,
  });
};

// 编辑转运单
const editTransport = (item) => {
  uni.navigateTo({
    url: `/pagesSub/case/transferinfo?id=${item.id}&edit=true`,
  });
};

// 创建转运单
const createTransport = () => {
  uni.navigateTo({
    url: "/pages/transport/create",
  });
};

// 数据加载函数
const loadInitialData = async () => {
  initLoading.value = true;
  try {
    await Promise.all([loadTransports(true), loadStats()]);
  } catch (error) {
    console.error("初始化数据失败:", error);
  } finally {
    initLoading.value = false;
  }
};

// 下拉刷新
const onRefresh = async () => {
  refreshing.value = true;
  pageNum.value = 1;
  await loadTransports(true);
  await loadStats();
  setTimeout(() => {
    refreshing.value = false;
  }, 1000);
};

// 加载更多
const onLoadMore = async () => {
  if (!hasMore.value || loading.value || loadingMore.value) return;
  await loadTransports();
};

// 加载转运单列表
const loadTransports = async (reset = false) => {
  if (reset) {
    pageNum.value = 1;
    hasMore.value = true;
  }

  if (loading.value || loadingMore.value || !hasMore.value) return;

  if (reset) {
    loading.value = true;
  } else {
    loadingMore.value = true;
  }

  try {
    const params = {
      pageNum: pageNum.value,
      pageSize: pageSize.value,
    };

    // 筛选条件
    if (currentType.value !== "") {
      params.transitStatus = currentType.value;
    }

    // 时间筛选条件
    if (startDate.value && endDate.value) {
      params.startDate = startDate.value;
      params.endDate = endDate.value;
    }

    console.log("请求参数:", params);

    const res = await uni.$uapi.post("/project/transport/list", params);

    console.log("接口返回数据:", res);

    if (res && res.code == 200) {
      // 处理不同的数据结构
      let data = [];

      if (res.rows && Array.isArray(res.rows)) {
        data = res.rows;
      } else if (res.data && Array.isArray(res.data)) {
        data = res.data;
      } else if (Array.isArray(res)) {
        data = res;
      } else {
        console.error("接口返回格式不正确:", res);
        throw new Error("接口返回格式不正确");
      }

      const mappedData = data.map((item) => mapApiDataToTransportItem(item));

      if (reset || pageNum.value == 1) {
        transports.value = mappedData;
      } else {
        transports.value = [...transports.value, ...mappedData];
      }

      // 判断是否还有更多数据
      hasMore.value = data.length >= pageSize.value;

      // 如果有数据加载成功，增加页码
      if (data.length > 0) {
        pageNum.value++;
      }
    } else {
      throw new Error(res?.msg || "加载失败");
    }
  } catch (error) {
    console.error("加载转运单列表失败:", error);
    uni.showToast({
      title: "网络请求失败",
      icon: "none",
    });
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

// 加载统计数据
const loadStats = async () => {
  try {
    const res = await uni.$uapi.post(
      "/project/transport/totalServiceTransportState",
    );
    console.log(222);
    console.log(res,'11');

    if (res.data && Array.isArray(res.data)) {
      // 使用 find 查找各状态的数量，避免硬编码索引
      const totalTransports =
        res.data.reduce((sum, item) => sum + item.count, 0) || 0;
      const pendingTransports =
        res.data.find((item) => item.transit_status == 1)?.count || 0;
      const completedTransports =
        res.data.find((item) => item.transit_status == 3)?.count || 0;

      stats.value = {
        totalTransports,
        pendingTransports,
        completedTransports,
      };
    } else {
      // 数据格式异常时设置默认值
      stats.value = {
        totalTransports: 0,
        pendingTransports: 0,
        completedTransports: 0,
      };
      console.error("统计数据格式异常:", res.data);
    }
  } catch (error) {
    console.error("加载统计失败:", error);
    // 出错时也设置默认值，避免页面显示异常
    stats.value = {
      totalTransports: 0,
      pendingTransports: 0,
      completedTransports: 0,
    };
  }
};

// 处理状态更新
const handleStatusUpdate = (update) => {
  const index = transports.value.findIndex(
    (item) => item.id == update.orderId,
  );
  if (index !== -1) {
    transports.value[index].transitStatus = update.status;
  }
};
</script>

<style lang="scss" scoped>
.transport-record {
  min-height: 100vh;
  background: linear-gradient(135deg, #fafdff 0%, #e3f0ff 100%);
  padding-bottom: 100rpx;

  .stats-card {
    margin: 20rpx;
    background: linear-gradient(135deg, #0071e3 0%, #2997ff 100%);
    border-radius: 32rpx;
    padding: 40rpx 30rpx;
    display: flex;
    align-items: center;
    box-shadow: 0 12px 48px 0 rgba(0, 113, 227, 0.15);

    .stat-item {
      flex: 1;
      text-align: center;

      .count {
        font-size: 40rpx;
        color: #fff;
        font-weight: bold;
        margin-bottom: 8rpx;
        display: block;
      }

      .label {
        font-size: 26rpx;
        color: rgba(255, 255, 255, 0.9);
      }
    }

    .divider {
      width: 2rpx;
      height: 60rpx;
      background: rgba(255, 255, 255, 0.2);
    }
  }

  .filter-bar {
    background: #fff;
    padding: 30rpx;
    margin: 0 20rpx 20rpx;
    border-radius: 24rpx;
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.05);

    .type-filter {
      display: flex;
      margin-bottom: 30rpx;
      background: #f8f9fa;
      border-radius: 16rpx;
      padding: 8rpx;

      text {
        flex: 1;
        text-align: center;
        font-size: 28rpx;
        color: #86868b;
        padding: 16rpx 0;
        position: relative;
        white-space: nowrap;
        transition: all 0.3s ease;

        &.active {
          color: #0071e3;
          font-weight: 600;
          background: #fff;
          border-radius: 12rpx;
          box-shadow: 0 2rpx 8rpx rgba(0, 113, 227, 0.15);

          &::after {
            content: "";
            position: absolute;
            left: 50%;
            bottom: 0;
            transform: translateX(-50%);
            width: 40rpx;
            height: 4rpx;
            background: #0071e3;
            border-radius: 2rpx;
          }
        }
      }
    }

    .date-filter {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 20rpx;

      picker {
        flex: 1;

        text {
          display: block;
          text-align: center;
          font-size: 26rpx;
          color: #515154;
          padding: 20rpx;
          background: #f8f9fa;
          border-radius: 16rpx;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          transition: all 0.3s ease;

          &:active {
            background: #e9ecef;
          }
        }
      }

      .separator {
        padding: 0 10rpx;
        font-size: 26rpx;
        color: #86868b;
        min-width: 40rpx;
        text-align: center;
      }
    }
  }

  .transport-list {
    height: calc(100vh - 420rpx);

    .transport-item {
      margin: 20rpx;
      background: linear-gradient(135deg, #fff 60%, #f5f6fa 100%);
      border-radius: 32rpx;
      padding: 40rpx 30rpx;
      margin-bottom: 24rpx;
      box-shadow: 0 12px 48px 0 rgba(0, 113, 227, 0.1);
      border: 1.5px solid #e5eaf0;
      transition: all 0.3s ease;

      &:active {
        transform: translateY(-2rpx);
        box-shadow: 0 16px 56px 0 rgba(0, 113, 227, 0.15);
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 30rpx;

        .case-info {
          .info {
            .case-no {
              font-size: 28rpx;
              font-weight: 600;
              color: #0071e3;
              margin-bottom: 8rpx;
              display: block;
            }

            .patient {
              font-size: 26rpx;
              color: #515154;
              font-weight: 500;
            }
          }
        }

        .status {
          font-size: 22rpx;
          padding: 8rpx 20rpx;
          border-radius: 20rpx;
          font-weight: 500;

          &.pending {
            color: #ff9500;
            background: rgba(255, 149, 0, 0.1);
          }

          &.transporting {
            color: #007aff;
            background: rgba(0, 122, 255, 0.1);
          }

          &.completed {
            color: #34c759;
            background: rgba(52, 199, 89, 0.1);
          }

          &.cancelled {
            color: #ff3b30;
            background: rgba(255, 59, 48, 0.1);
          }
        }
      }

      .detail-info {
        padding: 30rpx 0;
        border-top: 1.5px solid #f0f0f0;
        border-bottom: 1.5px solid #f0f0f0;

        .info-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 20rpx;
          align-items: flex-start;

          &:last-child {
            margin-bottom: 0;
          }

          .label {
            font-size: 26rpx;
            color: #86868b;
            min-width: 160rpx;
          }

          .value {
            font-size: 26rpx;
            color: #1d1d1f;
            flex: 1;
            text-align: right;
            font-weight: 500;
            line-height: 1.4;
          }
        }
      }

      .footer {
        padding-top: 30rpx;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .action-info {
          .label {
            font-size: 26rpx;
            color: #86868b;
            margin-right: 12rpx;
          }

          .transport-status {
            font-size: 28rpx;
            color: #0071e3;
            font-weight: 600;
          }
        }

        .actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12rpx;
          justify-content: flex-end;

          .action-btn {
            height: 58rpx;
            line-height: 58rpx;
            padding: 0 20rpx;
            font-size: 22rpx;
            border-radius: 29rpx;
            font-weight: 500;
            transition: all 0.3s ease;
            border: none;

            &.primary {
              color: #0071e3;
              background: rgba(0, 113, 227, 0.1);
            }

            &.success {
              color: #34c759;
              background: rgba(52, 199, 89, 0.1);
            }

            &.danger {
              color: #ff3b30;
              background: rgba(255, 59, 48, 0.1);
            }

            &.warning {
              color: #ff9500;
              background: rgba(255, 149, 0, 0.1);
            }

            &.secondary {
              color: #515154;
              background: #f5f5f7;
            }

            &:active {
              transform: scale(0.95);
              opacity: 0.8;
            }
          }
        }
      }
    }

    .load-more,
    .no-more {
      text-align: center;
      padding: 40rpx 0;
      color: #86868b;
      font-size: 28rpx;
    }

    .empty-state {
      padding: 120rpx 0;
      text-align: center;

      image {
        width: 240rpx;
        height: 240rpx;
        margin-bottom: 30rpx;
        opacity: 0.6;
      }

      text {
        font-size: 28rpx;
        color: #86868b;
      }
    }
  }

  /* 操作确认弹窗样式 */
  .modal-mask {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-content {
    background: #fff;
    border-radius: 32rpx;
    width: 620rpx;
    overflow: hidden;
    box-shadow: 0 20rpx 60rpx rgba(0, 0, 0, 0.2);
  }

  .modal-header {
    padding: 40rpx 40rpx 20rpx;
    text-align: center;
  }

  .modal-title {
    font-size: 34rpx;
    font-weight: 700;
    color: #1d1d1f;
  }

  .modal-body {
    padding: 20rpx 40rpx 40rpx;
    text-align: center;
  }

  .modal-desc {
    font-size: 28rpx;
    color: #515154;
    line-height: 1.6;
    display: block;
  }

  .modal-summary {
    margin-top: 24rpx;
    background: #f8f9fc;
    border-radius: 16rpx;
    padding: 20rpx;
    text-align: left;

    .summary-item {
      display: block;
      font-size: 24rpx;
      color: #86868b;
      line-height: 1.8;
    }
  }

  .modal-footer {
    display: flex;
    border-top: 1.5px solid #f0f0f0;
  }

  .modal-btn {
    flex: 1;
    height: 88rpx;
    line-height: 88rpx;
    font-size: 28rpx;
    border: none;
    border-radius: 0;
    background: transparent;
    transition: all 0.3s ease;

    &.cancel {
      color: #515154;
      border-right: 1.5px solid #f0f0f0;

      &:active {
        background: #f8f9fa;
      }
    }

    &.confirm {
      color: #0071e3;
      font-weight: 600;

      &:active {
        background: #f0f7ff;
      }
    }
  }
}
</style>
